import { motion } from "framer-motion";
import { useState } from "react";
import CircleIconAnimationButton from "../../components/ui/circle-icon-animation-button";

interface Location {
  id: number;
  name: string;
  address: string;
  map: string;
}

const locations: Location[] = [
  {
    id: 1,
    name: "Edmonton Office",
    address: "16115 72 Street Edmonton AB Canada T5Z 3T7",
    map: "https://www.google.com/maps?q=16115%2072%20Street%20Edmonton%20AB%20Canada&output=embed",
  },
  {
    id: 2,
    name: "Calgary Office",
    address: "15 Covemeadow Close NE Calgary AB Canada T3K 6B2",
    map: "https://www.google.com/maps?q=15%20Covemeadow%20Close%20NE%20Calgary%20AB%20Canada&output=embed",
  },
];

const LocationCTASection: React.FC = () => {
  const [activeLocation, setActiveLocation] = useState<Location>(locations[0]);

  const changeLocation = (location: Location) => {
    setActiveLocation(location);
  };

  return (
    <section className="w-full bg-[#111212] py-12 px-6 flex justify-center overflow-x-hidden">
      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: -160 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden md:translate-y-20 border-2 border-[#4c94ba]"
          >
            <video
              src="https://cdn.shopify.com/videos/c/o/v/16b5d0d285304eefbc0d2d8e42bbd993.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-65 object-cover"
            />

            <div className="absolute inset-0 flex items-end p-6 bg-linear-to-t from-black/80 via-black/40 to-transparent">
              <div>
                <h3 className="text-white font-semibold text-lg">
                  {activeLocation.name}
                </h3>
                <p className="text-gray-300 text-sm mt-1">
                  {activeLocation.address}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 160 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden border-2 md:-translate-y-20 border-[#4c94ba]"
          >
            <iframe
              title="Company Map"
              src={activeLocation.map}
              loading="lazy"
              className="w-full h-65 border-0"
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="flex flex-col gap-8"
        >
          <div>
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              Areas We Serve
            </h2>
            <p className="text-gray-400 mt-3">
              Select a location to view it on the map.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {locations.map((location) => (
              <div
                key={location.id}
                onClick={() => changeLocation(location)}
                className={`cursor-pointer p-5 rounded-xl border transition-all duration-300 ${
                  activeLocation.id === location.id
                    ? "border-[#4c94ba] bg-[#1a1b1b]"
                    : "border-white/10 hover:border-[#4c94ba]"
                }`}
              >
                <h3 className="text-white font-semibold text-lg">
                  {location.name}
                </h3>
                <p className="text-gray-400 text-sm mt-1">{location.address}</p>
              </div>
            ))}
          </div>

          <p className="text-gray-400">Let’s make it happen.</p>

          <div className="flex items-center gap-6">
            <CircleIconAnimationButton className="w-fit">
              Start the Conversation
            </CircleIconAnimationButton>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LocationCTASection;
